import {
	ITEM_CHARGE_TYPE,
	FREIGHT_MODE,
	ILane,
	TFuelLevyType,
	TRateCardModifierType,
	TTruckTypeModifier,
	TTruckTypeModifierDetails,
} from '@types';
import { z, ZodFormattedError } from 'zod';
import { formatDate } from '@utils';
import { getSelectedFuelLevy } from './getSelectedFuelLevy';
import { getLaneFuelLevyId } from './getLaneFuelLevyId';
import { isValid, parse } from 'date-fns';

const stringToNumberSchema = z
	.string()
	.refine((value) => value !== '', { message: 'Enter a value' })
	.refine((value) => {
		const num = Number(value);
		return !isNaN(num) && num >= 0;
	}, { message: 'Value must be at least 0' }
	);
const stringOrEmptyToNumberSchema = z
	.string()
	.refine((value) => {
		const num = Number(value);
		return !isNaN(num) && num >= 0;
	}, { message: 'Value must be at least 0' }
	);
const flatModifierSchema = z.object({
	type: z.enum([TRateCardModifierType.FIXED]),
	value: stringToNumberSchema
});

const marginsSchema = z.record(
	z.enum(['Ambient', 'Frozen', 'Chilled']),
	z.coerce.number().min(0).optional(),
);

const pricesSchema = z.record(
	z.enum(['Ambient', 'Frozen', 'Chilled']),
	z.coerce.number().min(0).optional(),
);

export const laneSchema = z.object({
	origin_location: z.object({
		text: z.string(),
		locality_type: z.enum(['POSTCODE', 'REGION'])
	}),
	destination_location: z.object({
		text: z.string(),
		locality_type: z.enum(['POSTCODE', 'REGION'])
	}),
	freight_mode: z.enum([FREIGHT_MODE.COASTAL, FREIGHT_MODE.RAIL, FREIGHT_MODE.ROAD]),
	rate_card_prices: z.array(z.object({
		margins: marginsSchema.nullable(),
		modifier_set: z.object({
			pallet_jack: flatModifierSchema,
			// Hot fix - 3/4 Will be implemented in phase 2
			/*  forklift: flatModifierSchema,
			    express: flatModifierSchema,
			    demurrage: flatModifierSchema, */
			demurrage: z.null(),
			forklift: z.object({
				type: z.enum([TRateCardModifierType.FIXED]),
				value: z.literal('0')
			}),
			express: z.object({
				type: z.enum([TRateCardModifierType.FIXED]),
				value: z.literal('0')
			}),
			hand_unload: z.object({
				type: z.enum([TRateCardModifierType.PER_STOP, TRateCardModifierType.PER_PALLET]),
				value: stringToNumberSchema,
				attracts_fuel_levy: z.boolean().optional(),
			}).nullable(),
			split_trailer: z.object({
				type: z.enum([TRateCardModifierType.PER_STOP]),
				truck_sizes: z.object({
					A_DOUBLE: z.object({ value: stringToNumberSchema }),
					B_DOUBLE: z.object({ value: stringToNumberSchema }),
					B_TRIPLE: z.object({ value: stringToNumberSchema }),
				}),
				attracts_fuel_levy: z.boolean().optional(),
			}).nullable(),
			pallet_exchange: flatModifierSchema,
			pallet_transfer: flatModifierSchema,
			tailgate_lift: z.object({
				type: z.enum([TRateCardModifierType.PER_LIFT, TRateCardModifierType.PER_STOP]),
				value: stringToNumberSchema,
				pallets_per_lift: stringToNumberSchema.optional(),
				max_charge: stringOrEmptyToNumberSchema.optional().nullable(),
			}).nullable(),
			day_surcharges: z.object({
				saturday: z.object({
					type: z.enum([TRateCardModifierType.PERCENT, TRateCardModifierType.PER_DAY]),
					value: stringToNumberSchema,
				}).nullable(),
				sunday: z.object({
					type: z.enum([TRateCardModifierType.PERCENT, TRateCardModifierType.PER_DAY]),
					value: stringToNumberSchema,
				}).nullable(),
				public_holiday: z.object({
					type: z.enum([TRateCardModifierType.PERCENT, TRateCardModifierType.PER_DAY]),
					value: stringToNumberSchema,
				}).nullable(),
			}),
			truck_type: z.object({
				details: z.object({
					TIPPER: z.object({
						type: z.string(),
						value: stringToNumberSchema,
					}),
					TAUTLINER: z.object({
						type: z.string(),
						value: stringToNumberSchema,
					}),
					TAUTLINER_DROP_DECK: z.object({
						type: z.string(),
						value: stringToNumberSchema,
					}),
					TAUTLINER_MEZZANINE_DECK: z.object({
						type: z.string(),
						value: stringToNumberSchema,
					}),
					TAUTLINER_STRAIGHT_DECK: z.object({
						type: z.string(),
						value: stringToNumberSchema,
					}),
					PANTECH: z.object({
						type: z.string(),
						value: stringToNumberSchema,
					}),
					FLAT_TOP: z.object({
						type: z.string(),
						value: stringToNumberSchema,
					}),
					SIDELOADER: z.object({
						type: z.string(),
						value: stringToNumberSchema,
					}),
					SKELETON_TRAILER: z.object({
						type: z.string(),
						value: stringToNumberSchema,
					}),
					OPEN_TOP: z.object({
						type: z.string(),
						value: stringToNumberSchema,
					}),
					UTE: z.object({
						type: z.string(),
						value: stringToNumberSchema,
					}),
				}),
			}),
			/* fuel_levy_adjustment: flatModifierSchema,
			   express: flatModifierSchema, */
			tolls: flatModifierSchema,
			admin_fee: flatModifierSchema,
			additional_pickup_stops: z.object({
				type: z.enum([TRateCardModifierType.FIXED, TRateCardModifierType.PER_STOP]),
				value: stringToNumberSchema,
				threshold: z.number().int().min(0),
			}).nullable(),
			additional_drop_off_stops: z.object({
				type: z.enum([TRateCardModifierType.FIXED, TRateCardModifierType.PER_STOP]),
				value: stringToNumberSchema,
				threshold: z.number().int().min(0),
			}).nullable(),
			dangerous_goods: z.object({
				type: z.enum([TRateCardModifierType.FIXED, TRateCardModifierType.PERCENT]),
				value: stringToNumberSchema,
			}).or(z.object({
				type: z.enum([TRateCardModifierType.THRESHOLD_KG]),
				value_below: stringOrEmptyToNumberSchema,
				value_above: stringOrEmptyToNumberSchema,
				threshold_kg: stringToNumberSchema,
			})).nullable()
		}),
		type: z.enum(['WEIGHT', 'PALLETS', 'FULL_LOAD', 'CPK', 'ITEM']),
		rangePrices: z.record(
			z.string(),
			pricesSchema
		).refine((obj) => Object.keys(obj).length > 0, { message: 'Range must have at least one entry', }),
		extra_attributes: z.object({
			priced_margin: z.number({ message: 'Enter a value or check box' }).min(-100, { message: 'Enter a valid value' }).max(100, { message: 'Enter a valid value' }).nullable(),
			priced_annualized_volume: z.number({ message: 'Enter a value or check box' }).min(0.01, { message: 'Enter a valid value' }).nullable()
		}),
		rules: z.object({
			allow_address_change: z.boolean(),
			allow_pickup_stops: z.boolean(),
			allow_weekend: z.boolean(),
			book_start_at_days: z.number().min(0),
			cpk_config_base_price: z.number().optional().nullable(),
			cpk_config_cubic_conversion: z.number().min(1).optional().nullable(),
			cpk_config_pallet_height: z.number().min(0).optional().nullable(),
			cpk_config_pallet_length: z.number().min(0).optional().nullable(),
			cpk_config_pallet_width: z.number().min(0).optional().nullable(),
			max_future_book_at_days: z.number().min(0),
			min_drop_at_days: z.number().min(0),
			booking_cutoff: z.string().min(1, { message: 'Cutoff time must be entered' }).optional().nullable(),
			allow_consolidation: z.boolean().optional(),
			disable_take_now_price: z.boolean(),
			min_charge: stringOrEmptyToNumberSchema,
		}),
		item_definition: z.object({
			item_id: z.string(),
			name: z.string().min(1, { message: 'Enter a name. This will be visible on the booking form.' }),
			weight_kg: stringOrEmptyToNumberSchema,
			length_cm: stringOrEmptyToNumberSchema,
			width_cm: stringOrEmptyToNumberSchema,
			height_cm: stringOrEmptyToNumberSchema,
			charge_type: z.nativeEnum(ITEM_CHARGE_TYPE, { message: 'Select one' }),
		}).optional()
	})),
	fuel_levy_type: z.enum(['global_shipper', 'global_carrier', 'customer', 'rate_card']),
	fuel_levy_id: z.string().optional(),
	fuel_levy_next_value: z.number().min(-100).max(100).nullable(),
	fuel_levy_next_start_at: z.string().min(1, { message: 'Next fuel levy start date is required' }).nullable(),
	end_date: z.string({ message: 'Enter an expiry date' }).min(1, { message: 'Enter an expiry date' }).optional(),
	minimum_billable_hours: stringToNumberSchema.nullable(),
	description: z.object({
		nick_name: z.string({ message: 'Enter a nickname' }).min(1, { message: 'Enter a nickname' }),
		description: z.string().nullable(),
	}),
	is_4pl: z.boolean().nullable(),
	is_contracted: z.boolean().nullable(),
	use_pallet_quantity_for_pricing: z.boolean(),
	shipper_tag: z.string().nullable(),
	type: z.enum(['admin', 'shipper', 'carrier']),
	linked_lane_external_id: z.string().nullable(),
	expected_updated_at: z.string().optional(),
}).superRefine((data, ctx) => {
	const rateCardTypes = data.rate_card_prices;

	if (data.is_4pl === null) {
		ctx.addIssue({
			code: z.ZodIssueCode.custom,
			message: 'Select one',
			path: ['is_4pl']
		})
	}
	if (data.is_contracted === null) {
		ctx.addIssue({
			code: z.ZodIssueCode.custom,
			message: 'Select one',
			path: ['is_contracted']
		})
	}
	rateCardTypes.forEach((rc, index) => {
		if (rc.modifier_set.tailgate_lift?.type === TRateCardModifierType.PER_LIFT) {
			if (!rc.modifier_set.tailgate_lift.pallets_per_lift) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: 'Enter a value',
					path: ['rate_card_prices', `${index}`, 'modifier_set', 'tailgate_lift', 'pallets_per_lift']
				})
			}
		}
		if (rc.modifier_set.tailgate_lift?.type) {
			if (!rc.modifier_set.tailgate_lift.value) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: 'Enter a value',
					path: ['rate_card_prices', `${index}`, 'modifier_set', 'tailgate_lift', 'value']
				})
			}
		}
		if (rc.modifier_set.dangerous_goods?.type === TRateCardModifierType.THRESHOLD_KG) {
			if (!rc.modifier_set.dangerous_goods.value_above && !rc.modifier_set.dangerous_goods.value_below) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: 'You need to enter a value for fee below or above',
					path: ['rate_card_prices', `${index}`, 'modifier_set', 'dangerous_goods', 'fees']
				})
			}
		}
		if (data.type !== 'carrier' && rc.rules.max_future_book_at_days > 60) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: 'Limit bookings to within cannot exceed 60 days',
				path: ['rate_card_prices', `${index}`, 'rules', 'max_future_book_at_days']
			})
		}

		const rangePricesErrorMessage = 'Ranges must be in ascending order without any gaps or overlap.'
		if (!Object.keys(rc.rangePrices).length) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: rangePricesErrorMessage,
				path: ['rate_card_prices', `${index}`, 'rangePrices']
			})
		} else {
			let ambientHasValue = false;
			let chilledHasValue = false;
			let frozenHasValue = false;
			Object.keys(rc.rangePrices).forEach((range, rangeIndex) => {
				if (rc.rangePrices[range].Ambient) {
					ambientHasValue = true;
				}
				if (rc.rangePrices[range].Chilled) {
					chilledHasValue = true;
				}
				if (rc.rangePrices[range].Frozen) {
					frozenHasValue = true;
				}
				if (rc.type !== 'FULL_LOAD') {
					const rangeSplit = range.split('-');
					const start = Number(rangeSplit[0]);
					const end = Number(rangeSplit[1]);
					if (!start && start !== 0) {
						ctx.addIssue({
							code: z.ZodIssueCode.custom,
							message: rangePricesErrorMessage,
							path: ['rate_card_prices', `${index}`, 'rangePrices']
						})
					}
					if (!end) {
						ctx.addIssue({
							code: z.ZodIssueCode.custom,
							message: rangePricesErrorMessage,
							path: ['rate_card_prices', `${index}`, 'rangePrices']
						})
					}
					if (end < start) {
						ctx.addIssue({
							code: z.ZodIssueCode.custom,
							message: rangePricesErrorMessage,
							path: ['rate_card_prices', `${index}`, 'rangePrices']
						})
					}
					const previousRange = Object.keys(rc.rangePrices)?.[rangeIndex - 1];
					if (previousRange) {
						const previousRangeSplit = previousRange.split('-');
						const previousRangeEnd = Number(previousRangeSplit[1]);
						if (previousRange && previousRangeEnd >= start) {
							ctx.addIssue({
								code: z.ZodIssueCode.custom,
								message: rangePricesErrorMessage,
								path: ['rate_card_prices', `${index}`, 'rangePrices']
							})
						} else if (previousRange && previousRangeEnd + 1 !== start) {
							ctx.addIssue({
								code: z.ZodIssueCode.custom,
								message: rangePricesErrorMessage,
								path: ['rate_card_prices', `${index}`, 'rangePrices']
							})
						}
					}
				}
			})
			if (ambientHasValue && rc.margins) {
				if (!rc.margins.Ambient) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: 'Cannot save with empty margin',
						path: ['rate_card_prices', `${index}`, 'rangePrices']
					});
				} else if (rc.margins.Ambient < 0) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: 'Cannot save with negative margin.',
						path: ['rate_card_prices', `${index}`, 'rangePrices']
					});
				}
			}
			if (chilledHasValue && rc.margins) {
				if (!rc.margins.Chilled) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: 'Cannot save with empty margin',
						path: ['rate_card_prices', `${index}`, 'rangePrices']
					});
				} else if (rc.margins.Chilled < 0) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: 'Cannot save with negative margin.',
						path: ['rate_card_prices', `${index}`, 'rangePrices']
					});
				}
			}
			if (frozenHasValue && rc.margins) {
				if (!rc.margins.Frozen) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: 'Cannot save with empty margin',
						path: ['rate_card_prices', `${index}`, 'rangePrices']
					});
				} else if (rc.margins.Frozen < 0) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: 'Cannot save with negative margin.',
						path: ['rate_card_prices', `${index}`, 'rangePrices']
					});
				}
			}
			if (!ambientHasValue && !chilledHasValue && !frozenHasValue) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: 'Enter at least one value or delete the rate card type to save.',
					path: ['rate_card_prices', `${index}`, 'rangePrices']
				});
			}
		}

		const { rules, item_definition: itemDefinition } = rc;
		if (
			(rules.cpk_config_cubic_conversion && rules.cpk_config_cubic_conversion > 1) ||
			(rc.type === 'ITEM')
		) {
			if (!rules.cpk_config_pallet_height) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: `Enter the height of the ${rc.type === 'CPK' ? 'pallet' : 'item.'}`,
					path: ['rate_card_prices', `${index}`, 'rules', 'cpk_config_pallet_height']
				})
			}
			if (!rules.cpk_config_pallet_length) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: `Enter the length of the ${rc.type === 'CPK' ? 'pallet' : 'item.'}`,
					path: ['rate_card_prices', `${index}`, 'rules', 'cpk_config_pallet_length']
				})
			}
			if (!rules.cpk_config_pallet_width) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: `Enter the width of the ${rc.type === 'CPK' ? 'pallet' : 'item.'}`,
					path: ['rate_card_prices', `${index}`, 'rules', 'cpk_config_pallet_width']
				})
			}
		}
		if (rc.type === 'ITEM' && itemDefinition?.charge_type === 'CPK' && !rules.cpk_config_cubic_conversion) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: `Cubic conversion is missing`,
				path: ['rate_card_prices', `${index}`, 'rules', 'cpk_config_cubic_conversion']
			})
		}
	})
});

export type TLaneSchema = z.infer<typeof laneSchema>;
const convertEmptyTruckTypesValueToZero = (truckTypeModifier: TTruckTypeModifier) => {
	const newObj: Record<string, { type: string; value: string | null | number }> = {};

	const trucks = truckTypeModifier.details;
	for (const key in trucks) {
		if (Object.prototype.hasOwnProperty.call(trucks, key)) {
			newObj[key] = {
				...trucks[key as keyof TTruckTypeModifierDetails],
				value: trucks[key as keyof TTruckTypeModifierDetails].value === '' || trucks[key as keyof TTruckTypeModifierDetails].value === null ? '0' : trucks[key as keyof TTruckTypeModifierDetails].value,
			};
		}
	}
	return {
		attracts_fuel_levy: !!truckTypeModifier.attracts_fuel_levy,
		details: newObj
	} as TTruckTypeModifier;
}

export const buildLaneAndValidate = (
	payload: ILane,
	type: 'admin' | 'shipper' | 'carrier',
	target: 'lane' |'price_preview' = 'lane',
	isLinkedRateCard: boolean = false,
	linked_lane_external_id: string = '',
): {
	success: boolean;
	errors: ZodFormattedError<TLaneSchema> | object,
	payload: TLaneSchema,
} => {
	const sortOrder = ['FULL_LOAD', 'PALLETS', 'WEIGHT', 'CPK', 'ITEM'];
	const sortedRateCards = [...payload.rate_cards].sort((a, b) => sortOrder.indexOf(a.type) - sortOrder.indexOf(b.type));

	const getFuelLevyValues = () => {
		const fuelLevyType = payload.fuel_levy_type as TFuelLevyType;
		let fuelLevyNextValue = null;
		let fuelLevyNextStartDate = null;

		/* Global or customer (shipper or carrier)
		   should not try to update */
		if (fuelLevyType === 'rate_card') {
			if (
				(
					typeof payload.fuel_levies[fuelLevyType]?.next?.tariff === 'number' ||
					payload.fuel_levies[fuelLevyType]?.next?.tariff
				) &&
				payload.fuel_levies[fuelLevyType]?.next?.start_at
			) {
				fuelLevyNextValue = Number(payload.fuel_levies[fuelLevyType]?.next?.tariff);
				fuelLevyNextStartDate = payload.fuel_levies[fuelLevyType]?.next?.start_at;
			} else if (payload.fuel_levies[fuelLevyType]?.current?.id) {
				/* To prevent saving existing fuel levy as new fuel levy
				   (date will be in the past and an error will be returned) */
				fuelLevyNextValue = null;
				fuelLevyNextStartDate = null;
			} else if (
				(
					typeof payload.fuel_levies[fuelLevyType]?.current?.tariff === 'number' ||
					payload.fuel_levies[fuelLevyType]?.current?.tariff
				) &&
				payload.fuel_levies[fuelLevyType]?.current?.start_at
			) {
				fuelLevyNextValue = Number(payload.fuel_levies[fuelLevyType]?.current?.tariff);
				fuelLevyNextStartDate = payload.fuel_levies[fuelLevyType]?.current?.start_at;
			}
		}

		// The API only accepts a levy id for the Customer types
		const laneFuelLevyId = getLaneFuelLevyId(payload);
		const selected = fuelLevyType === 'customer' && laneFuelLevyId
			? getSelectedFuelLevy(payload.fuel_levies, fuelLevyType, laneFuelLevyId)
			: null;

		return {
			fuelLevyType: (selected?.type as TFuelLevyType) ?? fuelLevyType,
			fuelLevyId: selected?.id ?? undefined,
			fuelLevyNextValue,
			fuelLevyNextStartDate
		}
	}

	let formatedEndDate;

	if (payload.end_date) {
		const parsedDate = parse(payload.end_date, 'dd/MM/yyyy', new Date());
		if (isValid(parsedDate)) {
			formatedEndDate = formatDate(parsedDate, 'API_DATE_FORMAT')
		} else {
			formatedEndDate = formatDate(new Date(payload.end_date), 'API_DATE_FORMAT')
		}
	}

	const lanePayload: TLaneSchema = {
		freight_mode: payload.freight_mode,
		is_4pl: payload.is_4pl,
		is_contracted: payload.is_contracted,
		use_pallet_quantity_for_pricing: payload.use_pallet_quantity_for_pricing || false,
		origin_location: {
			text: payload.origin_location.text,
			locality_type: payload.origin_location.locality_type as 'POSTCODE' | 'REGION'
		},
		destination_location: {
			text: payload.destination_location.text,
			locality_type: payload.destination_location.locality_type as 'POSTCODE' | 'REGION'
		},
		fuel_levy_type: getFuelLevyValues().fuelLevyType,
		...(getFuelLevyValues().fuelLevyId ? { fuel_levy_id: getFuelLevyValues().fuelLevyId } : {}),
		fuel_levy_next_value: getFuelLevyValues().fuelLevyNextValue,
		fuel_levy_next_start_at: getFuelLevyValues().fuelLevyNextStartDate,
		end_date: formatedEndDate,
		rate_card_prices: sortedRateCards
			.filter((rc) => !['HOURS'].includes(rc.type)) // Keeping this in case existing hourly ratecards
			.map((rc) => {
				return {
					...rc,
					rangePrices: Array.isArray(rc.rangePrices) ? {} : rc.rangePrices,
					modifier_set: {
						...rc.modifier_set,
						admin_fee: {
							...rc.modifier_set.admin_fee,
							value: rc.modifier_set?.admin_fee?.value || '0' // Why do we need this?
						},
						tolls: {
							...rc.modifier_set.tolls,
							value: rc.modifier_set?.tolls?.value || '0' // Why do we need this?
						},
						truck_type: convertEmptyTruckTypesValueToZero(rc.modifier_set.truck_type),
						demurrage: null,
						express: {
							type: TRateCardModifierType.FIXED,
							value: '0'
						},
						forklift: {
							type: TRateCardModifierType.FIXED,
							value: '0'
						}
					},
					extra_attributes: {
						priced_annualized_volume: type === 'carrier' ? null : rc.extra_attributes.priced_annualized_volume ?
							parseFloat(Number(rc.extra_attributes.priced_annualized_volume).toFixed(4)) : rc.extra_attributes.priced_annualized_volume,
						priced_margin: type === 'carrier' ? null : rc.extra_attributes.priced_margin ?
							parseFloat(Number(rc.extra_attributes.priced_margin).toFixed(4)) : rc.extra_attributes.priced_margin
					},
					rules: {
						...rc.rules,
						cpk_config_cubic_conversion: !rc.rules.cpk_config_cubic_conversion ? null : rc.rules.cpk_config_cubic_conversion,
						...((rc.type === 'ITEM' || rc.rules.cpk_config_cubic_conversion) ? {
							cpk_config_pallet_height: rc.rules.cpk_config_pallet_height || (rc.type === 'ITEM' ? (Number(rc.item_definition?.height_cm ?? 0) * 100) : 0),
							cpk_config_pallet_length: rc.rules.cpk_config_pallet_length || (rc.type === 'ITEM' ? (Number(rc.item_definition?.length_cm ?? 0) * 100) : 0),
							cpk_config_pallet_width: rc.rules.cpk_config_pallet_width || (rc.type === 'ITEM' ? (Number(rc.item_definition?.width_cm ?? 0) * 100) : 0),
						} : {
							cpk_config_pallet_height: null,
							cpk_config_pallet_length: null,
							cpk_config_pallet_width: null
						}),
						disable_take_now_price: rc.type === 'ITEM' ? true : rc.rules.disable_take_now_price || false,
						allow_weekend: rc.rules.allow_weekend || false,
						allow_consolidation: rc.rules.allow_consolidation || false,
						min_charge: `${rc.rules.min_charge || 0}`
					},
					margins: type === 'carrier' ? null : rc.margins,
					...(rc.item_definition ?
						{
							item_definition: {
								...rc.item_definition,
								weight_kg: `${rc.item_definition?.weight_kg}`,
								width_cm: `${rc.item_definition?.width_cm}`,
								length_cm: `${rc.item_definition?.length_cm}`,
								height_cm: `${rc.item_definition?.height_cm}`,
							}
						}
						: {})
				}
			}),
		minimum_billable_hours: typeof payload.minimum_billable_hours === 'number' ? `${payload.minimum_billable_hours}` : payload.minimum_billable_hours,
		description: {
			description: payload.description,
			nick_name: payload.nick_name,
		},
		shipper_tag: payload.shipper_tag?.external_id ?? null,
		type,
		linked_lane_external_id: linked_lane_external_id,
	}

	const result = laneSchema.safeParse(lanePayload);

	if (target === 'lane' && payload.external_id && !isLinkedRateCard) {
		// Patch endpoint returns an error if it receives this ones
		const keysToRemove = ['freight_mode', 'origin_location', 'destination_location'];
		keysToRemove.forEach((key) => delete lanePayload[key as keyof TLaneSchema]);
		lanePayload.expected_updated_at = payload.updated_at;
	}

	return {
		success: result.success,
		errors: !result.success ? result.error.format() : {},
		payload: lanePayload,
	}

}